import React, { useState } from 'react';
import { caseAgentService } from '../services/api';
import { formatCaseStatus } from '../utils/caseStatus';
import '../styles/CaseAgentWidget.css';

export default function CaseAgentWidget() {
  const [session, setSession] = useState(null);
  const [mode, setMode] = useState('existing');
  const [startForm, setStartForm] = useState({ caseNumber: '', clientEmail: '', customerName: '' });
  const [newCaseForm, setNewCaseForm] = useState({
    customerName: '',
    clientEmail: '',
    clientPhone: '',
    caseType: '',
    jurisdiction: '',
    opposingParty: '',
    preferredContactTime: '',
    description: '',
  });
  const [message, setMessage] = useState('');
  const [pendingMessage, setPendingMessage] = useState('');
  const [aiPreview, setAiPreview] = useState(null);
  const [uploadForm, setUploadForm] = useState({ file: null, documentType: 'Evidence', notes: '' });
  const [uploadInputKey, setUploadInputKey] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const updateStartForm = (e) => {
    const { name, value } = e.target;
    setStartForm((prev) => ({ ...prev, [name]: value }));
  };

  const updateNewCaseForm = (e) => {
    const { name, value } = e.target;
    setNewCaseForm((prev) => ({ ...prev, [name]: value }));
  };

  const startSession = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await caseAgentService.startSession(startForm);
      setSession(response.data);
    } catch (err) {
      const responseMessage = typeof err.response?.data === 'string'
        ? err.response.data
        : err.response?.data?.message;
      setError(responseMessage || 'Could not verify your case. Please check the case number and email.');
    } finally {
      setLoading(false);
    }
  };

  const createCase = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await caseAgentService.createCase(newCaseForm);
      setSession(response.data);
    } catch (err) {
      const responseMessage = typeof err.response?.data === 'string'
        ? err.response.data
        : err.response?.data?.message;
      setError(responseMessage || 'Could not create your inquiry. Please check the required fields.');
    } finally {
      setLoading(false);
    }
  };

  const previewMessage = async (e) => {
    e.preventDefault();
    if (!message.trim()) {
      return;
    }

    const nextMessage = message.trim();
    setLoading(true);
    setError('');
    try {
      const response = await caseAgentService.previewAiRequest(session.accessToken, nextMessage);
      setPendingMessage(nextMessage);
      setAiPreview(response.data);
    } catch (err) {
      setError('Could not prepare the AI information-sharing preview. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!pendingMessage) {
      return;
    }

    const optimisticMessage = { role: 'customer', content: pendingMessage };
    setSession((prev) => ({ ...prev, messages: [...(prev.messages || []), optimisticMessage] }));
    setMessage('');
    setPendingMessage('');
    setAiPreview(null);
    setLoading(true);
    setError('');

    try {
      const response = await caseAgentService.sendMessage(session.accessToken, optimisticMessage.content);
      setSession((prev) => ({
        ...prev,
        status: response.data.status,
        missingInformation: response.data.missingInformation,
        messages: response.data.messages,
      }));
    } catch (err) {
      setError('Failed to send your message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const updateUploadForm = (e) => {
    const { name, value, files } = e.target;
    setUploadForm((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
    }));
  };

  const uploadDocument = async (e) => {
    e.preventDefault();
    if (!uploadForm.file) {
      setError('Choose a document before uploading.');
      return;
    }

    setUploading(true);
    setError('');
    try {
      const response = await caseAgentService.uploadDocument(
        session.accessToken,
        uploadForm.file,
        uploadForm.documentType,
        uploadForm.notes
      );
      setSession(response.data);
      setUploadForm({ file: null, documentType: 'Evidence', notes: '' });
      setUploadInputKey((prev) => prev + 1);
    } catch (err) {
      const responseMessage = typeof err.response?.data === 'string'
        ? err.response.data
        : err.response?.data?.message;
      setError(responseMessage || 'Failed to upload your document. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="case-agent-page">
      <div className="case-agent-shell">
        <div className="case-agent-header">
          <strong>Libra Legal</strong>
          <span>Case Assistant</span>
        </div>

        {!session ? (
          <div className="case-agent-card">
            <div className="agent-mode-toggle" role="tablist" aria-label="Case assistant mode">
              <button
                type="button"
                className={mode === 'existing' ? 'active' : ''}
                onClick={() => {
                  setMode('existing');
                  setError('');
                }}
              >
                Existing Case
              </button>
              <button
                type="button"
                className={mode === 'new' ? 'active' : ''}
                onClick={() => {
                  setMode('new');
                  setError('');
                }}
              >
                New Inquiry
              </button>
            </div>
            <h1>{mode === 'existing' ? 'Access Your Case Assistant' : 'Start a New Case Inquiry'}</h1>
            <p>
              {mode === 'existing'
                ? 'Enter the case number and email on file to continue.'
                : 'Share the basics and the Libra Legal team will receive a new case record.'}
            </p>
            {error && <div className="agent-error">{error}</div>}
            {mode === 'existing' ? (
              <form className="agent-form" onSubmit={startSession}>
                <label>
                  Case Number
                  <input name="caseNumber" value={startForm.caseNumber} onChange={updateStartForm} required />
                </label>
                <label>
                  Email on File
                  <input name="clientEmail" type="email" value={startForm.clientEmail} onChange={updateStartForm} required />
                </label>
                <label>
                  Your Name
                  <input name="customerName" value={startForm.customerName} onChange={updateStartForm} />
                </label>
                <button type="submit" disabled={loading}>{loading ? 'Verifying...' : 'Start Chat'}</button>
              </form>
            ) : (
              <form className="agent-form new-case-form" onSubmit={createCase}>
                <div className="agent-form-grid">
                  <label>
                    Full Name
                    <input name="customerName" value={newCaseForm.customerName} onChange={updateNewCaseForm} required />
                  </label>
                  <label>
                    Email
                    <input name="clientEmail" type="email" value={newCaseForm.clientEmail} onChange={updateNewCaseForm} required />
                  </label>
                  <label>
                    Phone
                    <input name="clientPhone" value={newCaseForm.clientPhone} onChange={updateNewCaseForm} />
                  </label>
                  <label>
                    Case Type
                    <input name="caseType" value={newCaseForm.caseType} onChange={updateNewCaseForm} placeholder="Family, civil, contract..." />
                  </label>
                  <label>
                    Jurisdiction / Location
                    <input name="jurisdiction" value={newCaseForm.jurisdiction} onChange={updateNewCaseForm} />
                  </label>
                  <label>
                    Opposing Party
                    <input name="opposingParty" value={newCaseForm.opposingParty} onChange={updateNewCaseForm} />
                  </label>
                </div>
                <label>
                  Preferred Contact Time
                  <input name="preferredContactTime" value={newCaseForm.preferredContactTime} onChange={updateNewCaseForm} />
                </label>
                <label>
                  What happened?
                  <textarea
                    name="description"
                    value={newCaseForm.description}
                    onChange={updateNewCaseForm}
                    required
                    rows="5"
                    placeholder="Share dates, people involved, deadlines, and what help you need."
                  />
                </label>
                <button type="submit" disabled={loading}>
                  {loading ? 'Creating Inquiry...' : 'Create Case & Start Chat'}
                </button>
              </form>
            )}
          </div>
        ) : (
          <div className="case-agent-card chat-card">
            <div className="case-agent-summary">
              <div>
                <span>Case</span>
                <strong>{session.caseNumber}</strong>
              </div>
              <div>
                <span>Status</span>
                <strong>{formatCaseStatus(session.status)}</strong>
              </div>
            </div>
            <h1>{session.caseTitle}</h1>
            {session.missingInformation?.length > 0 && (
              <div className="missing-info">
                <span>Helpful information still needed</span>
                <p>{session.missingInformation.join(', ')}</p>
              </div>
            )}
            <div className="agent-messages">
              {(session.messages || []).map((item, index) => (
                <div key={`${item.id || index}-${item.role}`} className={`agent-message ${item.role}`}>
                  {item.content}
                </div>
              ))}
            </div>
            {error && <div className="agent-error">{error}</div>}
            {aiPreview && (
              <section className="agent-ai-preview" aria-live="polite">
                <div className="agent-ai-preview-heading">
                  <span>AI Information Preview</span>
                  <strong>{aiPreview.providerName}</strong>
                </div>
                {aiPreview.modelName && <p><strong>Model:</strong> {aiPreview.modelName}</p>}
                <p><strong>Information shared:</strong></p>
                <ul>
                  {(aiPreview.includedData || []).map((item) => <li key={item}>{item}</li>)}
                </ul>
                <pre>{aiPreview.prompt}</pre>
                <p className="agent-ai-preview-notice">{aiPreview.disclosureNotice}</p>
                <div className="agent-ai-preview-actions">
                  <button type="button" className="agent-preview-cancel" onClick={() => {
                    setPendingMessage('');
                    setAiPreview(null);
                  }}>Cancel</button>
                  <button type="button" onClick={sendMessage} disabled={loading}>
                    {aiPreview.providerName === 'No external AI provider configured' ? 'Save Message' : 'Send to AI Provider'}
                  </button>
                </div>
              </section>
            )}
            <form className="agent-upload" onSubmit={uploadDocument}>
              <div className="agent-upload-header">
                <strong>Upload a document</strong>
                <span>Files are attached to this case for the legal team.</span>
              </div>
              <div className="agent-upload-grid">
                <label>
                  Document
                  <input key={uploadInputKey} name="file" type="file" onChange={updateUploadForm} />
                </label>
                <label>
                  Type
                  <select name="documentType" value={uploadForm.documentType} onChange={updateUploadForm}>
                    <option value="Evidence">Evidence</option>
                    <option value="Identification">Identification</option>
                    <option value="Contract">Contract</option>
                    <option value="Correspondence">Correspondence</option>
                    <option value="Court Filing">Court Filing</option>
                    <option value="Other">Other</option>
                  </select>
                </label>
              </div>
              <label>
                Notes
                <input
                  name="notes"
                  value={uploadForm.notes}
                  onChange={updateUploadForm}
                  placeholder="Optional context for the legal team"
                />
              </label>
              <button type="submit" disabled={uploading || !uploadForm.file}>
                {uploading ? 'Uploading...' : 'Attach Document'}
              </button>
            </form>
            <form className="agent-composer" onSubmit={previewMessage}>
              <input
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Share an update or answer a question"
              />
              <button type="submit" disabled={loading || !message.trim() || aiPreview}>
                {loading ? 'Preparing...' : 'Review Before Sending'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
